import { flow } from 'fp-ts/function';
import { connect, useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useEffect } from 'react';
import { Card, ModalBodyWrapper, ModalWrapper, SmallContentContainer, FieldsContainer } from '@mews-commander/ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ApplicationState, CommanderThunkDispatch, Option, WithEnterpriseId } from '@mews-commander/types';
import { useResolveRoute } from '@mews/routing';
import { commanderForm, presence, SelectField, validation } from '@mews-commander/legacy-form';
import { fetchRates } from '@mews-commander/legacy-entities/src/ducks/exchangeRates';
import { getAvailableNonExchangeCurrencyOptions, getExchangeRateIndexUrl } from '../../reducers';
import ModalHeader from '../../../../legacy/React/Components/Common/ModalHeader';
import { addExchangeRate, setExchangeRates } from '../../actions';
import { Breadcrumbs } from '../../../../components/BreadcrumbList';

const ExchangeRateFormFields = ({ currencyOptions }: { currencyOptions: Option[] }) => (
    <FieldsContainer>
        <SelectField
            label={<LocalizedText textKey={TextKey.Currency} />}
            options={currencyOptions}
            name="currencyCode"
        />
    </FieldsContainer>
);

const constraints = {
    currencyCode: {
        presence,
    },
};

interface ExchangeRateFormStateProps {
    currencyOptions: Option[];
}

export interface ExchangeRateFormOwnProps {
    scope: string;
    form: string;
}

const ExchangeRateForm = flow(
    commanderForm({
        submitButtonProps: { variant: 'primary' },
        submitLabel: <LocalizedText textKey={TextKey.Create} />,
        validate: validation(constraints),
    }),
    connect<ExchangeRateFormStateProps, {}, ExchangeRateFormOwnProps, ApplicationState>(
        createStructuredSelector({
            currencyOptions: getAvailableNonExchangeCurrencyOptions,
        }),
        (dispatch: CommanderThunkDispatch, { scope }) => ({
            onSubmit: (values: $TSFixMe) => dispatch(addExchangeRate({ ...values, scope })),
        })
    )
)(ExchangeRateFormFields);

const ExchangeRateFormContainer = ({ enterpriseId }: WithEnterpriseId) => {
    const dispatch: CommanderThunkDispatch = useDispatch();
    const resolveRoute = useResolveRoute();

    const breadcrumbUrl = useSelector((state) => getExchangeRateIndexUrl(state, { enterpriseId, resolveRoute }));

    const breadcrumbs: Breadcrumbs = [
        {
            text: <LocalizedText textKey={TextKey.CurrencyAcceptancePlural} />,
            url: breadcrumbUrl,
        },
        {
            text: <LocalizedText textKey={TextKey.CurrencyAcceptance} />,
            active: true,
        },
    ];

    useEffect(() => {
        void dispatch(fetchRates({ enterpriseId })).then(() => dispatch(setExchangeRates(enterpriseId)));
    }, [dispatch, enterpriseId]);

    const form = `exchange-rate-create-${enterpriseId}`;

    return (
        <ModalWrapper>
            <ModalHeader title={<LocalizedText textKey={TextKey.CurrencyAcceptance} />} breadcrumbs={breadcrumbs} />
            <ModalBodyWrapper>
                <SmallContentContainer condensed>
                    <Card>
                        <ExchangeRateForm scope={enterpriseId} form={form} />
                    </Card>
                </SmallContentContainer>
            </ModalBodyWrapper>
        </ModalWrapper>
    );
};

export default ExchangeRateFormContainer;
